import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import FormikInput from "components/formikInput";
import { CourseModule } from "slices/courseSlice";

interface Module extends CourseModule {
  content: any[];
}

interface Assessment {
  id?: string;
  title: string;
  description?: string;
  moduleID?: string;
  passingScore: number;
  timeLimit?: number;
  unlocksModuleID?: string;
}

interface AssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Assessment>) => void;
  assessment: Assessment | null;
  modules: Module[];
}

export function AssessmentDialog({
  open,
  onClose,
  onSave,
  assessment,
  modules,
}: AssessmentDialogProps): React.JSX.Element {
  console.log(modules)
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{assessment ? "Edit Assessment" : "Add New Assessment"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text" mb={2}>
          Create an assessment to test student knowledge. After saving, you can add MCQ questions by clicking &quot;Edit Questions&quot;.
        </Typography>
        <Formik
          initialValues={{
            title: assessment?.title || "",
            description: assessment?.description || "",
            moduleID: assessment?.moduleID || "",
            passingScore: assessment?.passingScore || 70,
            timeLimit: assessment?.timeLimit || 0,
            unlocksModuleID: assessment?.unlocksModuleID || "",
          }}
          validationSchema={Yup.object({
            title: Yup.string().required("Assessment title is required"),
            passingScore: Yup.number().min(0).max(100).required("Passing score is required"),
          })}
          onSubmit={(values) => {
            onSave(values);
            onClose();
          }}
          enableReinitialize
        >
          {({ submitForm }) => (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormikInput
                    name="title"
                    label="Assessment Title *"
                    placeholder="e.g., Module 1 Quiz"
                    size="large"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikInput
                    name="description"
                    label="Assessment Description"
                    placeholder="Describe what this assessment covers..."
                    multiline
                    rows={3}
                    size="large"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikInput
                    name="moduleID"
                    type="dropdown"
                    label="Associated Module (Optional)"
                    options={[
                      ...modules.map((m, i) => ({ value: m.id || i.toString(), label: m.title })),
                    ]}
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Link this assessment to a specific module
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikInput
                    name="unlocksModuleID"
                    type="dropdown"
                    label="Unlocks Module (Optional)"
                    options={[
                      { value: "", label: "None - Does not unlock modules" },
                      ...modules.map((m, i) => ({ value: m.id || i.toString(), label: m.title })),
                    ]}
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Module that unlocks when this assessment is passed
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormikInput
                    name="passingScore"
                    type="number"
                    label="Passing Score (%) *"
                    placeholder="70"
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Minimum score required to pass (0-100)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormikInput
                    name="timeLimit"
                    type="number"
                    label="Time Limit (minutes)"
                    placeholder="0 (no limit)"
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Set 0 for unlimited time
                  </Typography>
                </Grid>

              </Grid>
              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={submitForm} variant="gradient" color="info">
                  {assessment ? "Update Assessment" : "Create Assessment"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

